import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { LessonMode } from '../../../Constants';
import NumberLineService from '../../services/NumberLineService';
import HtmlComponent from '../HtmlComponent';
import Guideline from '../tools/Guideline';

import '../../../css/NumberLine.scss';
import '../../../css/Accessibility.scss';
import useStyleEvents from '../../../hooks/useStyleEvents';
import { register } from '../../../i18n';
import PrintQuestionNumber from './PrintQuestionNumber';

const t = register('GlobalQuestionLabels');

const NumberLine = observer(({ lessonElementId }) => {
  const {
    numberLineManager,
    questionFeedbackManager,
    responseManager,
    lessonManager,
    toolbarManager
  } = useContext(MobXProviderContext);

  const model = lessonManager.getSlideModel(lessonElementId);
  const numberLineService = useMemo(() => new NumberLineService(model), [lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [rerender, setRerender] = useState(false);

  const radioGroupRef = useRef(null);

  // Function to get proper labels for element types
  const getElementTypeLabel = (elementType) => {
    const labels = {
      'point': t('point', 'Point'),
      'segment': t('segment', 'Segment'),
      'segment_left_point_hollow': t('segmentLeftPointHollow', 'Segment with left point hollow'),
      'segment_right_point_hollow': t('segmentRightPointHollow', 'Segment with right point hollow'),
      'segment_both_points_hollow': t('segmentBothPointsHollow', 'Segment with both points hollow'),
      'ray_right_direction': t('rayRightDirection', 'Ray pointing right'),
      'ray_right_direction_point_hollow': t('rayRightDirectionPointHollow', 'Ray pointing right with hollow point'),
      'ray_left_direction': t('rayLeftDirection', 'Ray pointing left'),
      'ray_left_direction_point_hollow': t('rayLeftDirectionPointHollow', 'Ray pointing left with hollow point')
    };
    return labels[elementType] || elementType;
  };

  useEffect(() => {
    const init = async () => {
      await numberLineService.initialize();
      await numberLineService.runtimeObjectReady();
      await numberLineService.showResponse();

      setRerender(!rerender);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set focus to first radio button when component loads
  useEffect(() => {
    if (model.elementTypes && model.elementTypes.length > 0) {
      const firstRadioButton = document.querySelector('.numberLineBottomControls button[role="radio"]');
      if (firstRadioButton && !numberLineManager.elementEditMode) {
        // Set the first available element type as default if none is selected
        const firstElementType = model.elementTypes.find(type => 
          numberLineService.ELEMENT_TYPES.includes(type)
        );
        if (firstElementType) {
          numberLineService.setElementEditMode(firstElementType);
        }
      }
    }
  }, [model.elementTypes, numberLineManager.elementEditMode, numberLineService]);

  // Update tabindex values when elementEditMode changes and ensure focus
  useEffect(() => {
    if (radioGroupRef.current) {
      const radioButtons = radioGroupRef.current.querySelectorAll('button[role="radio"]');
      console.log('Found radio buttons:', radioButtons.length);
      
      radioButtons.forEach(button => {
        const buttonElementType = button.getAttribute('data-element-type');
        const isSelected = buttonElementType === numberLineManager.elementEditMode;
        button.setAttribute('tabindex', isSelected ? '0' : '-1');
        
        // Focus the selected button
        if (isSelected) {
          button.focus();
        }
      });
    }
  }, [numberLineManager.elementEditMode]);

  const feedbackState = questionFeedbackManager.getUiState(model.lessonElementId);
  if (feedbackState.showAnswerFeedback) {
    if (numberLineService.canvas) {
      numberLineService.showResponse();
      numberLineService.showValidationFeedback();
    }
  }

  if (feedbackState.showAnswers) {
    if (numberLineService.canvas) {
      numberLineService.drawCorrectAnswers();
    }
  }

  const lessonElementState = responseManager.getLessonElementState(model.lessonElementId);
  if (lessonElementState.isTryAgain) {
    numberLineService.showResponse();
  }

  const handleClear = () => {
    if (!feedbackState.showAnswerFeedback && !feedbackState.showAnswers) {
      numberLineService.clearAll();
    }
  };

  const handleDelete = () => {
    if (!feedbackState.showAnswerFeedback && !feedbackState.showAnswers) {
      numberLineService.deleteSelectedElement();
    }
  };

  const setActiveElementType = (elementType) => {
    if (!feedbackState.showAnswerFeedback && !feedbackState.showAnswers) {
      numberLineService.setElementEditMode(elementType);
    }
  };

  // Handle keyboard navigation for radio buttons
  const handleRadioKeyDown = (event, currentElementType) => {
    console.log('Key pressed:', event.key, 'on element:', currentElementType);
    
    if (!radioGroupRef.current) return;
    
    const radioButtons = Array.from(radioGroupRef.current.querySelectorAll('button[role="radio"]'));
    const currentIndex = radioButtons.findIndex(button => 
      button.getAttribute('data-element-type') === currentElementType
    );
    
    if (currentIndex === -1) return;
    
    let nextIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = (currentIndex + 1) % radioButtons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = currentIndex === 0 ? radioButtons.length - 1 : currentIndex - 1;
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        setActiveElementType(currentElementType);
        return;
      default:
        return;
    }
    
    const nextButton = radioButtons[nextIndex];
    if (nextButton) {
      console.log('Moving to next button:', nextButton.getAttribute('data-element-type'));
      nextButton.focus();
      // Also select the next button
      const nextElementType = nextButton.getAttribute('data-element-type');
      setActiveElementType(nextElementType);
    }
  };

  useStyleEvents(lessonElementId);

  return (
    <div className='element-container' id={`_${numberLineService.getId()}`}>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {numberLineManager.elementEditMode ? 
          `${getElementTypeLabel(numberLineManager.elementEditMode)} selected` : 
          'No element type selected'}
      </div>
      <div className='testItemQuestion'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={model.directions || model.questionText || ''} />
      </div>
      <div className='editSection'>
        <div className='sideBySide graphControls'>
          <button className='clearButton' onClick={handleClear}>{t('clearAll')}</button>
          <button className='deleteElementButton' onClick={handleDelete}>{t('deleteElement')}</button>
        </div>
        <div className='body canvasOrganizerBody' style={{ height: numberLineService.getHeight(), width: model.graphWidth }}>
          <canvas className='canvas' id={numberLineService.getCanvasId()} />
        </div>
        <div className='bottomControlsContainer'>
          <div 
            ref={radioGroupRef}
            className='numberLineBottomControls' 
            role='radiogroup' 
            aria-label={t('elementTypeSelection', 'Element type selection')}
          >
            {numberLineService.ELEMENT_TYPES.map((elementType, index) => { // eslint-disable-line array-callback-return
              if (model.elementTypes.includes(elementType)) {
                const isSelected = numberLineManager.elementEditMode === elementType;
                return (
                  <button
                    role='radio'
                    key={elementType}
                    className={`elementTypeButton icon ${elementType} ${isSelected ? 'active' : ''}`}
                    data-element-type={elementType}
                    aria-checked={isSelected}
                    aria-label={getElementTypeLabel(elementType)}
                    tabIndex={isSelected ? 0 : -1}
                    type="button"
                    onClick={() => setActiveElementType(elementType)}
                    onFocus={() => console.log('Button focused:', elementType)}
                    onKeyDown={(event) => handleRadioKeyDown(event, elementType)} />
                );
              } else {
                return (<></>);
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

export default NumberLine;
